import { createBrowserRouter, Navigate } from 'react-router-dom';

// ============================================================================
// LAYOUTS (from src/components/layout)
// ============================================================================
import RootLayout from '@/components/layout/RootLayout';
import AuthLayout from '@/components/layout/AuthLayout';
import FamilyLayout from '@/components/layout/FamilyLayout';
import AttorneyLayout from '@/components/layout/AttorneyLayout';
import VendorLayout from '@/components/layout/VendorLayout';
import AdminLayout from '@/components/layout/AdminLayout';
import PublicLayout from '@/components/layout/PublicLayout';

// ============================================================================
// AUTH GUARDS (from src/features/auth/components)
// ============================================================================
import { AuthGuard } from '@/features/auth/components/AuthGuard';
import { RoleGuard } from '@/features/auth/components/RoleGuard';

// ============================================================================
// STANDALONE PAGES (need to be created in src/app/pages/)
// ============================================================================
import LandingPage from '@/app/pages/LandingPage';
import NotFoundPage from '@/app/pages/NotFoundPage';

// ============================================================================
// AUTH PAGES (from src/features/auth)
// ============================================================================
import {
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmailPage,
} from '@/features/auth';

// ============================================================================
// FAMILY PAGES (from src/features/family)
// ============================================================================
import {
  DashboardPage as FamilyDashboardPage,
  SettingsPage,
} from '@/features/family';

// ============================================================================
// CHILDREN PAGES (from src/features/children)
// ============================================================================
import {
  ChildrenPage,
  ChildProfilePage,
} from '@/features/children';

// ============================================================================
// HEALTH PAGES (from src/features/health)
// ============================================================================
import {
  HealthDashboardPage,
  HealthTrackingPage,
  HealthLogsPage,
  HealthLogDetailPage,
  HealthLogFormPage,
  HealthTimelinePage,
  HealthReportsPage,
  HealthExportPage,
  MedicationsPage,
} from '@/features/health';

// ============================================================================
// MEDICATIONS PAGES (from src/features/medications)
// ============================================================================
import {
  MedicationDetailPage,
} from '@/features/medications';

// ============================================================================
// GOALS PAGES (from src/features/goals)
// ============================================================================
import {
  GoalsPage,
  FamilyGoalsPage,
  GoalDetailPage,
  GoalFormPage,
  IEPReportPage,
} from '@/features/goals';

// ============================================================================
// CARE TEAM PAGES (from src/features/care-team)
// ============================================================================
import {
  CareTeamPage,
  CareTeamMemberPage,
} from '@/features/care-team';

// ============================================================================
// DOCUMENTS PAGES (from src/features/documents)
// ============================================================================
import {
  DocumentsPage,
  DocumentDetailPage,
  DocumentSearchPage,
  DocumentVaultPage,
} from '@/features/documents';

// ============================================================================
// EXPENSES PAGES (from src/features/expenses)
// ============================================================================
import {
  ExpensesPage,
  ExpenseDetailPage,
  ExpenseFormPage,
  ExpenseReportsPage,
  BudgetsPage,
} from '@/features/expenses';

// ============================================================================
// CALENDAR PAGES (from src/features/calendar)
// ============================================================================
import {
  CalendarPage,
  AgendaPage,
  AppointmentDetailPage,
  AppointmentFormPage,
} from '@/features/calendar';

// ============================================================================
// COMMUNITY PAGES (from src/features/community)
// ============================================================================
import {
  CommunityPage,
} from '@/features/community';

// ============================================================================
// NOTIFICATIONS PAGES (from src/features/notifications)
// ============================================================================
import {
  NotificationsPage,
} from '@/features/notifications';

// ============================================================================
// RESOURCES PAGES (from src/features/resources)
// ============================================================================
import {
  ResourceLibraryPage,
  ResourceDetailPage,
  ResourceCategoryPage,
  ResourceSearchPage,
  DiagnosisHubPage,
  SavedResourcesPage,
  FamilyResourcesPage,
} from '@/features/resources';

// ============================================================================
// MARKETPLACE PAGES (from src/features/marketplace)
// ============================================================================
import {
  MarketplacePage,
  CategoryPage as MarketplaceCategoryPage,
  ProductDetailPage,
  VendorProfilePage as MarketplaceVendorPage,
  VendorListPage,
  SearchResultsPage as MarketplaceSearchPage,
  FamilyMarketplacePage,
  OrdersPage as MarketplaceOrdersPage,
  OrderDetailPage,
} from '@/features/marketplace';

// ============================================================================
// ATTORNEY PAGES (from src/features/attorney)
// ============================================================================
import {
  AttorneyDashboardPage,
  AttorneyProfilePage,
  CasesPage,
  CaseDetailPage,
  CaseDocumentsPage,
  CaseExpensesPage,
  CaseTimelinePage,
  ClientsPage,
  ClientPortalPage,
  LifeCarePlanPage,
  SettlementPlannerPage,
} from '@/features/attorney';

// ============================================================================
// VENDOR PAGES (from src/features/vendor)
// ============================================================================
import {
  VendorDashboardPage,
  VendorProfilePage,
  VendorProductsPage,
  VendorProductFormPage,
  VendorOrdersPage,
  VendorOrderDetailPage,
  VendorReviewsPage,
  VendorAnalyticsPage,
} from '@/features/vendor';

// ============================================================================
// ADMIN PAGES (from src/features/admin)
// ============================================================================
import {
  AdminDashboardPage,
  AdminUsersPage,
  AdminUserDetailPage,
  AdminFamiliesPage,
  AdminFamilyDetailPage,
  AdminModerationPage,
  AdminVendorsPage,
  AdminResourcesPage,
  AdminResourceEditorPage,
  AdminAnalyticsPage,
  AdminSystemPage,
  AdminAuditPage,
} from '@/features/admin';

// ============================================================================
// ROUTER CONFIGURATION
// ============================================================================

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <NotFoundPage />,
    children: [
      // ======================================================================
      // PUBLIC ROUTES (No Auth Required)
      // ======================================================================

      // Landing Page
      { index: true, element: <LandingPage /> },

      // Auth Routes
      {
        element: <AuthLayout />,
        children: [
          { path: 'login', element: <LoginPage /> },
          { path: 'register', element: <RegisterPage /> },
          { path: 'forgot-password', element: <ForgotPasswordPage /> },
          { path: 'reset-password', element: <ResetPasswordPage /> },
          { path: 'verify-email', element: <VerifyEmailPage /> },
        ],
      },

      // Public Resources
      {
        path: 'resources',
        element: <PublicLayout />,
        children: [
          { index: true, element: <ResourceLibraryPage /> },
          { path: 'search', element: <ResourceSearchPage /> },
          { path: 'category/:categorySlug', element: <ResourceCategoryPage /> },
          { path: 'diagnosis/:diagnosisSlug', element: <DiagnosisHubPage /> },
          { path: ':slug', element: <ResourceDetailPage /> },
        ],
      },

      // Public Marketplace
      {
        path: 'marketplace',
        element: <PublicLayout />,
        children: [
          { index: true, element: <MarketplacePage /> },
          { path: 'search', element: <MarketplaceSearchPage /> },
          { path: 'category/:slug', element: <MarketplaceCategoryPage /> },
          { path: 'vendors', element: <VendorListPage /> },
          { path: 'vendors/:slug', element: <MarketplaceVendorPage /> },
          { path: 'products/:slug', element: <ProductDetailPage /> },
        ],
      },

      // ======================================================================
      // FAMILY PORTAL (Auth Required)
      // ======================================================================
      {
        path: 'family',
        element: (
          <AuthGuard>
            <RoleGuard allowedRoles={['family', 'admin']}>
              <FamilyLayout />
            </RoleGuard>
          </AuthGuard>
        ),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: <FamilyDashboardPage /> },

          // Children Management
          { path: 'children', element: <ChildrenPage /> },
          { path: 'children/:childId', element: <ChildProfilePage /> },

          // Child Health Routes
          { path: 'children/:childId/health', element: <HealthDashboardPage /> },
          { path: 'children/:childId/health/logs', element: <HealthLogsPage /> },
          { path: 'children/:childId/health/logs/new', element: <HealthLogFormPage /> },
          { path: 'children/:childId/health/logs/:logId', element: <HealthLogDetailPage /> },
          { path: 'children/:childId/health/logs/:logId/edit', element: <HealthLogFormPage /> },
          { path: 'children/:childId/health/medications', element: <MedicationsPage /> },
          { path: 'children/:childId/health/medications/:medicationId', element: <MedicationDetailPage /> },
          { path: 'children/:childId/health/timeline', element: <HealthTimelinePage /> },
          { path: 'children/:childId/health/reports', element: <HealthReportsPage /> },
          { path: 'children/:childId/health/export', element: <HealthExportPage /> },

          // Child Goals Routes
          { path: 'children/:childId/goals', element: <GoalsPage /> },
          { path: 'children/:childId/goals/new', element: <GoalFormPage /> },
          { path: 'children/:childId/goals/:goalId', element: <GoalDetailPage /> },
          { path: 'children/:childId/goals/:goalId/edit', element: <GoalFormPage /> },
          { path: 'children/:childId/iep', element: <IEPReportPage /> },

          // Health Tracking (General)
          { path: 'health', element: <HealthTrackingPage /> },
          { path: 'health/logs', element: <HealthLogsPage /> },
          { path: 'health/logs/new', element: <HealthLogFormPage /> },
          { path: 'health/logs/:logId', element: <HealthLogDetailPage /> },
          { path: 'health/logs/:logId/edit', element: <HealthLogFormPage /> },
          { path: 'health/medications', element: <MedicationsPage /> },
          { path: 'health/medications/:medicationId', element: <MedicationDetailPage /> },
          { path: 'health/timeline', element: <HealthTimelinePage /> },
          { path: 'health/reports', element: <HealthReportsPage /> },
          { path: 'health/export', element: <HealthExportPage /> },

          // Goals (General)
          { path: 'goals', element: <FamilyGoalsPage /> },

          // Care Team
          { path: 'care-team', element: <CareTeamPage /> },
          { path: 'care-team/:memberId', element: <CareTeamMemberPage /> },

          // Documents
          { path: 'documents', element: <DocumentsPage /> },
          { path: 'documents/search', element: <DocumentSearchPage /> },
          { path: 'documents/vault', element: <DocumentVaultPage /> },
          { path: 'documents/:documentId', element: <DocumentDetailPage /> },

          // Expenses & Budgets
          { path: 'expenses', element: <ExpensesPage /> },
          { path: 'expenses/new', element: <ExpenseFormPage /> },
          { path: 'expenses/reports', element: <ExpenseReportsPage /> },
          { path: 'expenses/budgets', element: <BudgetsPage /> },
          { path: 'expenses/:expenseId', element: <ExpenseDetailPage /> },
          { path: 'expenses/:expenseId/edit', element: <ExpenseFormPage /> },

          // Calendar & Appointments
          { path: 'calendar', element: <CalendarPage /> },
          { path: 'calendar/agenda', element: <AgendaPage /> },
          { path: 'calendar/appointments/new', element: <AppointmentFormPage /> },
          { path: 'calendar/appointments/:appointmentId', element: <AppointmentDetailPage /> },
          { path: 'calendar/appointments/:appointmentId/edit', element: <AppointmentFormPage /> },

          // Resources (Family)
          { path: 'resources', element: <FamilyResourcesPage /> },
          { path: 'resources/saved', element: <SavedResourcesPage /> },
          { path: 'resources/search', element: <ResourceSearchPage /> },
          { path: 'resources/category/:categorySlug', element: <ResourceCategoryPage /> },
          { path: 'resources/:slug', element: <ResourceDetailPage /> },

          // Marketplace (Family)
          { path: 'marketplace', element: <FamilyMarketplacePage /> },
          { path: 'marketplace/orders', element: <MarketplaceOrdersPage /> },
          { path: 'marketplace/orders/:orderId', element: <OrderDetailPage /> },
          { path: 'marketplace/products/:productId', element: <ProductDetailPage /> },

          // Community
          { path: 'community', element: <CommunityPage /> },

          // Notifications
          { path: 'notifications', element: <NotificationsPage /> },

          // Settings
          { path: 'settings', element: <SettingsPage /> },
        ],
      },

      // ======================================================================
      // ATTORNEY PORTAL
      // ======================================================================
      {
        path: 'attorney',
        element: (
          <AuthGuard>
            <RoleGuard allowedRoles={['attorney', 'admin']}>
              <AttorneyLayout />
            </RoleGuard>
          </AuthGuard>
        ),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: <AttorneyDashboardPage /> },

          // Cases
          { path: 'cases', element: <CasesPage /> },
          { path: 'cases/:caseId', element: <CaseDetailPage /> },
          { path: 'cases/:caseId/documents', element: <CaseDocumentsPage /> },
          { path: 'cases/:caseId/expenses', element: <CaseExpensesPage /> },
          { path: 'cases/:caseId/timeline', element: <CaseTimelinePage /> },
          { path: 'cases/:caseId/life-care-plan', element: <LifeCarePlanPage /> },
          { path: 'cases/:caseId/settlement', element: <SettlementPlannerPage /> },

          // Clients
          { path: 'clients', element: <ClientsPage /> },
          { path: 'clients/:clientId', element: <ClientPortalPage /> },

          // Profile
          { path: 'profile', element: <AttorneyProfilePage /> },
        ],
      },

      // ======================================================================
      // VENDOR PORTAL
      // ======================================================================
      {
        path: 'vendor',
        element: (
          <AuthGuard>
            <RoleGuard allowedRoles={['vendor', 'admin']}>
              <VendorLayout />
            </RoleGuard>
          </AuthGuard>
        ),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: <VendorDashboardPage /> },

          // Products
          { path: 'products', element: <VendorProductsPage /> },
          { path: 'products/new', element: <VendorProductFormPage /> },
          { path: 'products/:productId', element: <VendorProductFormPage /> },
          { path: 'products/:productId/edit', element: <VendorProductFormPage /> },

          // Orders
          { path: 'orders', element: <VendorOrdersPage /> },
          { path: 'orders/:orderId', element: <VendorOrderDetailPage /> },

          // Reviews
          { path: 'reviews', element: <VendorReviewsPage /> },

          // Analytics
          { path: 'analytics', element: <VendorAnalyticsPage /> },

          // Profile
          { path: 'profile', element: <VendorProfilePage /> },
        ],
      },

      // ======================================================================
      // ADMIN PORTAL
      // ======================================================================
      {
        path: 'admin',
        element: (
          <AuthGuard>
            <RoleGuard allowedRoles={['admin']}>
              <AdminLayout />
            </RoleGuard>
          </AuthGuard>
        ),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: <AdminDashboardPage /> },

          // Users
          { path: 'users', element: <AdminUsersPage /> },
          { path: 'users/:userId', element: <AdminUserDetailPage /> },

          // Families
          { path: 'families', element: <AdminFamiliesPage /> },
          { path: 'families/:familyId', element: <AdminFamilyDetailPage /> },

          // Moderation
          { path: 'moderation', element: <AdminModerationPage /> },

          // Vendors
          { path: 'vendors', element: <AdminVendorsPage /> },

          // Resources
          { path: 'resources', element: <AdminResourcesPage /> },
          { path: 'resources/new', element: <AdminResourceEditorPage /> },
          { path: 'resources/:resourceId', element: <AdminResourceEditorPage /> },
          { path: 'resources/:resourceId/edit', element: <AdminResourceEditorPage /> },

          // Analytics
          { path: 'analytics', element: <AdminAnalyticsPage /> },

          // System
          { path: 'system', element: <AdminSystemPage /> },

          // Audit
          { path: 'audit', element: <AdminAuditPage /> },
        ],
      },

      // ======================================================================
      // 404 CATCH-ALL
      // ======================================================================
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

export default router;